import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  BRAND as DEFAULT_BRAND,
  IMAGES as DEFAULT_IMAGES,
  TREATMENTS as DEFAULT_TREATMENTS,
  LOCATIONS as DEFAULT_LOCATIONS,
  REVIEWS as DEFAULT_REVIEWS,
  CONTENT as DEFAULT_CONTENT,
  FAQS as DEFAULT_FAQS,
} from "@/data/site";

const DEFAULT_OFFERS = {
  active: true,
  title: "This month's specials",
  subtitle: "Limited-time offers",
  items: [
    { name: "Hot stones back massage", price: "£40" },
    { name: "Full body Thai compress massage", price: "£65" },
    { name: "Lash lift and tint", price: "£35" },
    { name: "Manicure and pedicure", price: "£65" },
  ],
  footer_note: "Add shellac gel polish £5 extra",
};

const DEFAULT_EVENT = {
  active: true,
  eyebrow: "Special skincare event",
  title: "Dr. Hauschka Express Facial",
  dates_label: "Friday 7th August & Saturday 8th August",
  start_date: "2026-08-07",
  end_date: "2026-08-08",
  price_label: "Only £15",
  inclusions: [
    "Skin analysis to discuss any skin concerns and advise on skincare routines and products at home",
    "Deep cleanse",
    "Tone",
    "Nourishing Dr. Hauschka face mask",
    "Mini facial massage",
    "Finished with Dr. Hauschka under eye cream and day cream",
  ],
  outcome: "Leaving your skin feeling glowing & rejuvenated.",
  badge: "100% Natural · Effective · Holistic",
  upgrade_title: "Exclusive event upgrade",
  upgrade_description: "Add a 30-minute Swedish back massage for just £25",
  upgrade_saving_label: "That's a saving of £40!",
  location_line: "Located at Elixir Health & Wellbeing, Molesworth Street, Wadebridge",
  tagline: "Treat your skin. Indulge your senses. Feel your best.",
  footer_note: "Limited appointment spaces — booking is essential.",
  cta_label: "Book my £15 facial",
  cta_special_name: "Dr. Hauschka Express Facial (Skincare Event)",
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULTS = {
  brand: DEFAULT_BRAND,
  images: DEFAULT_IMAGES,
  treatments: DEFAULT_TREATMENTS,
  locations: DEFAULT_LOCATIONS,
  reviews: DEFAULT_REVIEWS,
  offers: DEFAULT_OFFERS,
  event: DEFAULT_EVENT,
  content: DEFAULT_CONTENT,
  faqs: DEFAULT_FAQS,
};

const SettingsContext = createContext({
  settings: DEFAULTS,
  loading: true,
  refresh: () => {},
});

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      const data = res.data || {};
      setSettings({
        brand: { ...DEFAULT_BRAND, ...(data.brand || {}) },
        images: { ...DEFAULT_IMAGES, ...(data.images || {}) },
        treatments:
          data.treatments && (data.treatments.massage || data.treatments.beauty)
            ? data.treatments
            : DEFAULT_TREATMENTS,
        locations: Array.isArray(data.locations) && data.locations.length ? data.locations : DEFAULT_LOCATIONS,
        reviews: Array.isArray(data.reviews) && data.reviews.length ? data.reviews : DEFAULT_REVIEWS,
        offers: data.offers && Object.keys(data.offers).length ? data.offers : DEFAULT_OFFERS,
        event: data.event && Object.keys(data.event).length ? data.event : DEFAULT_EVENT,
        content: {
          home: { ...DEFAULT_CONTENT.home, ...(data.content?.home || {}) },
          about: { ...DEFAULT_CONTENT.about, ...(data.content?.about || {}) },
        },
        faqs: Array.isArray(data.faqs) && data.faqs.length ? data.faqs : DEFAULT_FAQS,
      });
    } catch {
      // Network/API issue – keep defaults rendered so the site still works.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <SettingsContext.Provider value={{ settings, loading, refresh }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext).settings;
export const useSettingsContext = () => useContext(SettingsContext);
